import {
  Box,
  CircularProgress,
  ClickAwayListener,
  List,
  ListItemButton,
  Paper,
  Popper,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  isPlacesConfigured,
  PlaceDetails,
  PlacePrediction,
  PlacesSession,
  shouldQueryPlaces,
} from "src/Shared/Tools/GooglePlaces";

type Props = {
  label?: string;
  value: string | null | undefined;
  onChange: (address: string) => void;
  /**
   * Called when a suggestion is picked, with the place resolved to coordinates and a country — or
   * null when that lookup fails. Typing never calls this: only a deliberate selection has a place
   * behind it. The site form uses it to settle the TDDA Region (bug 5695).
   */
  onPlaceSelected?: (details: PlaceDetails | null) => void;
  /** Biases the lookup to the client's country; both TDDA countries when unknown. */
  countryCode?: string | null;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  placeholder?: string;
};

const LOOKUP_DEBOUNCE_MS = 300;

/**
 * The single-string site address with Google-backed lookup (S3.1, story 5599).
 *
 * Suggestions appear from two characters, debounced and grouped into one billing session; picking
 * one writes the formatted address into the field. Arrow keys move through the list, Enter takes the
 * highlighted suggestion, Escape and a click outside close it, and typed text is always kept.
 */
const AddressAutocomplete: React.FC<Props> = ({
  label,
  value,
  onChange,
  onPlaceSelected,
  countryCode,
  disabled,
  required,
  error,
  helperText,
  placeholder,
}) => {
  const session = useMemo(() => new PlacesSession(), []);
  const [options, setOptions] = useState<PlacePrediction[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [highlight, setHighlight] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [unavailable, setUnavailable] = useState<boolean>(!isPlacesConfigured());
  const timer = useRef<any>(null);
  const alive = useRef<boolean>(true);
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      clearTimeout(timer.current);
    };
  }, []);

  const lookup = (term: string) => {
    clearTimeout(timer.current);
    if (!shouldQueryPlaces(term)) {
      setOptions([]);
      setOpen(false);
      setLoading(false);
      setHighlight(0);
      return;
    }

    setLoading(true);
    timer.current = setTimeout(async () => {
      const ready = await session.ready();
      if (!alive.current) return;
      if (!ready) {
        // No key, or the script could not load: say so once and stay a plain text field.
        setUnavailable(true);
        setLoading(false);
        return;
      }

      const predictions = await session.predict(term, countryCode);
      if (!alive.current) return;
      setOptions(predictions);
      setHighlight(0);
      setOpen(predictions.length > 0);
      setLoading(false);
    }, LOOKUP_DEBOUNCE_MS);
  };

  const onSelect = (selected: PlacePrediction) => {
    const address = selected.description;
    onChange(address);
    setOptions([]);
    setOpen(false);
    setHighlight(0);

    // Free text typed and committed has no place behind it, so there is nothing to resolve.
    const placeId = selected.placeId;
    if (placeId && onPlaceSelected) {
      // Resolved before the session is renewed, so the details call is billed as part of the
      // same lookup session as the prediction that produced this id.
      session
        .details(placeId, address)
        .then((details) => {
          if (alive.current) onPlaceSelected(details);
        })
        .finally(() => session.renew());
      return;
    }

    // The selection ends this lookup session; the next keystroke starts a new one.
    session.renew();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open || options.length === 0) {
      return;
    }

    // Arrow keys move the highlight and wrap; Enter selects; Escape closes suggestions only.
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((prev) => (prev + 1) % options.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((prev) => (prev - 1 + options.length) % options.length);
    } else if (e.key === "Enter") {
      if (highlight >= 0 && highlight < options.length) {
        e.preventDefault();
        onSelect(options[highlight]);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      if (e.nativeEvent) {
        e.nativeEvent.stopImmediatePropagation?.();
        e.nativeEvent.stopPropagation?.();
      }
      setOpen(false);
    }
  };

  // A validation error outranks the lookup notice: the notice is advice, the error is why the save
  // was refused (bug 5694).
  const lookupHelperText = error
    ? helperText
    : unavailable
      ? "Address lookup unavailable — type the address as it should read."
      : helperText;

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box ref={anchorRef} sx={{ position: "relative", width: "100%" }}>
        <TextField
          fullWidth
          label={label}
          value={value || ""}
          required={required}
          disabled={disabled}
          error={error}
          helperText={lookupHelperText}
          placeholder={placeholder}
          autoComplete="off"
          onChange={(e) => {
            const next = e.target.value;
            onChange(next);
            lookup(next);
          }}
          onKeyDown={onKeyDown}
          onFocus={() => {
            if (options.length > 0) setOpen(true);
          }}
          InputProps={{
            endAdornment: loading ? (
              <CircularProgress size={16} />
            ) : null,
          }}
          inputProps={{
            role: "combobox",
            "aria-autocomplete": "list",
            "aria-expanded": open && options.length > 0,
            "aria-controls": "address-suggestions-list",
            "aria-activedescendant":
              open && options.length > 0 && highlight >= 0
                ? `address-suggestion-${highlight}`
                : undefined,
          }}
        />
        <Popper
          open={open && options.length > 0}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{
            zIndex: 1300,
            width: anchorRef.current ? anchorRef.current.clientWidth : "100%",
          }}
        >
          <Paper elevation={4} sx={{ maxHeight: 300, overflowY: "auto", mt: 0.5 }}>
            <List dense id="address-suggestions-list" role="listbox">
              {options.map((option, index) => (
                <ListItemButton
                  id={`address-suggestion-${index}`}
                  role="option"
                  aria-selected={index === highlight}
                  key={option.placeId || index}
                  selected={index === highlight}
                  onMouseEnter={() => setHighlight(index)}
                  onClick={() => onSelect(option)}
                >
                  <Typography variant="body2">{option.description}</Typography>
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default AddressAutocomplete;
